<template>
  <el-card>
    <el-form
      ref="form"
      label-position="top"
      label-width="auto"
      :model="contact"
      :rules="rules"
      :validate-on-rule-change="false"
      @submit.prevent
    >
      <el-row :gutter="20">
        <!-- Type -->
        <el-form-item :error="errors.type" prop="type" label="Type">
          <el-radio-group v-model="contact.type">
            <el-radio-button
              v-for="contactType in ContactTypeLabels"
              :key="contactType.id"
              :value="contactType.id"
            >
              {{ contactType.label }}
            </el-radio-button>
          </el-radio-group>
        </el-form-item>
      </el-row>

      <el-divider />

      <!-- PERSONAL START -->
      <template v-if="contact.type === ContactType.Personal">
        <el-row :gutter="20">
          <p class="block-title">
            Personal info
            <span v-if="contact.verified">
              <el-tag type="success">Verified</el-tag>
            </span>
            <span v-if="contact.verification_submitted">
              <el-tag type="warning">Verification submitted</el-tag>
            </span>
            <span v-if="contact.verification_failed">
              <el-tag type="danger">Verification failed</el-tag>
            </span>
            <span v-if="contact.verification_not_finished">
              <el-tag type="warning">Verification not finished</el-tag>
            </span>
          </p>
          <!-- Last Name -->
          <el-col :span="12" :xs="24">
            <el-form-item
              prop="last_name"
              :error="errors.last_name"
              label="Last name"
            >
              <el-input
                v-model="contact.last_name"
                type="text"
                :disabled="loading"
              />
            </el-form-item>
          </el-col>

          <!-- First Name -->
          <el-col :span="12" :xs="24">
            <el-form-item
              prop="first_name"
              :error="errors.first_name"
              label="First name"
            >
              <el-input
                v-model="contact.first_name"
                type="text"
                :disabled="loading"
              />
            </el-form-item>
          </el-col>

          <!-- Middle Name -->
          <el-col :span="12" :xs="24">
            <el-form-item
              prop="middle_name"
              :error="errors.middle_name"
              label="Middle name"
            >
              <el-input
                v-model="contact.middle_name"
                type="text"
                :disabled="loading"
              />
            </el-form-item>
          </el-col>

          <!-- Birthday -->
          <el-col :span="12" :xs="24">
            <el-form-item
              prop="birthday"
              :error="errors.birthday"
              label="Birthday"
            >
              <el-date-picker
                v-model="contact.birthday"
                type="date"
                :clearable="false"
                value-format="YYYY-MM-DD"
                :disabled="loading"
                style="width: 100% !important"
                placeholder="YYYY-MM-DD"
              />
            </el-form-item>
          </el-col>

          <!-- Country -->
          <el-col :span="12" :xs="24">
            <el-form-item
              prop="country_code"
              :error="errors.country_code"
              label="Country"
            >
              <CountrySelect
                v-model="contact.country_code"
                :countries="countries"
                :loading="loading"
              />
            </el-form-item>
          </el-col>

          <!-- VAT -->
          <el-col v-if="vatIsVisible" :span="12" :xs="24">
            <el-form-item
              prop="vat"
              label="European VAT ID"
              :error="errors.vat"
            >
              <template #label>
                <span>
                  European VAT ID
                  <IconWithHint>
                    <a
                      target="_blank"
                      href="https://support.traforama.com/en/articles/how-should-the-vat-number-look-like"
                    >
                      How should the VAT number look like? &#8594;Click
                      me&#8592;
                    </a>
                  </IconWithHint>
                </span>
              </template>
              <el-input
                v-model="contact.vat"
                type="text"
                :disabled="loading || !haveVat"
                placeholder="XX12345678901"
              >
                <template #append>
                  <el-switch
                    v-model="haveVat"
                    inactive-color="#ff4949"
                    :inactive-text="
                      haveVat
                        ? 'I have VAT number'
                        : 'I don\'t have a VAT number'
                    "
                  />
                </template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider />

        <el-row :gutter="20">
          <p class="block-title">Contact Info</p>

          <!-- Email -->
          <el-col :span="12" :xs="24">
            <el-form-item
              prop="email"
              :error="errors.email"
              label="Billing Email"
            >
              <el-input
                v-model="contact.email"
                type="email"
                :disabled="loading"
              />
            </el-form-item>
          </el-col>

          <!-- Phone -->
          <el-col :span="12" :xs="24">
            <el-form-item prop="phone" :error="errors.phone" label="Phone">
              <el-input
                v-model="contact.phone"
                type="text"
                :disabled="loading"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </template>
      <!-- PERSONAL END -->

      <!-- CORPORATE START -->
      <template v-if="contact.type === ContactType.Corporate">
        <el-row :gutter="20">
          <p class="block-title">General Info</p>
          <!-- Company name -->
          <el-col :span="12" :xs="24">
            <el-form-item
              prop="company_name"
              label="Company name"
              :error="errors.company_name"
            >
              <el-input
                v-model="contact.company_name"
                type="text"
                :disabled="loading"
              />
            </el-form-item>
          </el-col>

          <!-- Country -->
          <el-col :span="12" :xs="24">
            <el-form-item
              prop="country_code"
              :error="errors.country_code"
              label="Country"
            >
              <CountrySelect
                v-model="contact.country_code"
                :countries="countries"
                :loading="loading"
              />
            </el-form-item>
          </el-col>

          <!-- Reg number -->
          <el-col :span="12" :xs="24">
            <el-form-item
              prop="registration_number"
              label="Reg. no"
              :error="errors.registration_number"
            >
              <el-input
                v-model="contact.registration_number"
                type="text"
                :disabled="loading"
              />
            </el-form-item>
          </el-col>

          <!-- VAT -->
          <el-col v-if="vatIsVisible" :span="12" :xs="24">
            <el-form-item
              prop="vat"
              label="European VAT ID"
              :error="errors.vat"
            >
              <template #label>
                <span>
                  European VAT ID
                  <IconWithHint>
                    <a
                      target="_blank"
                      href="https://support.traforama.com/en/articles/how-should-the-vat-number-look-like"
                    >
                      How should the VAT number look like? &#8594;Click
                      me&#8592;
                    </a>
                  </IconWithHint>
                </span>
              </template>
              <el-input
                v-model="contact.vat"
                type="text"
                :disabled="loading || !haveVat"
                placeholder="XX12345678901"
              >
                <template #append>
                  <el-switch
                    v-model="haveVat"
                    inactive-color="#ff4949"
                    :inactive-text="
                      haveVat
                        ? 'I have VAT number'
                        : 'I don\'t have a VAT number'
                    "
                  />
                </template>
              </el-input>
            </el-form-item>
          </el-col>

          <!-- TAX -->
          <el-col v-if="tinIsVisible" :span="12" :xs="24">
            <el-form-item prop="tin" label="TAX ID" :error="errors.tin">
              <el-input v-model="contact.tin" type="text" :disabled="loading" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <p class="block-title">Contact Info</p>

          <!-- First name -->
          <el-col :span="12" :xs="24">
            <el-form-item
              prop="representative_first_name"
              label="Representative First Name"
              :error="errors.representative_first_name"
            >
              <el-input
                v-model="contact.representative_first_name"
                type="text"
                :disabled="loading"
              />
            </el-form-item>
          </el-col>

          <!-- Last name -->
          <el-col :span="12" :xs="24">
            <el-form-item
              prop="representative_last_name"
              label="Representative Last Name"
              :error="errors.representative_last_name"
            >
              <el-input
                v-model="contact.representative_last_name"
                type="text"
                :disabled="loading"
              />
            </el-form-item>
          </el-col>

          <!-- Title -->
          <el-col :span="12" :xs="24">
            <el-form-item
              prop="position"
              label="Representative Title"
              :error="errors.position"
            >
              <el-input
                v-model="contact.position"
                type="text"
                :disabled="loading"
              />
            </el-form-item>
          </el-col>

          <!-- Email -->
          <el-col :span="12" :xs="24">
            <el-form-item
              prop="email"
              :error="errors.email"
              label="Billing Email"
            >
              <el-input
                v-model="contact.email"
                type="email"
                :disabled="loading"
              />
            </el-form-item>
          </el-col>

          <!-- Phone -->
          <el-col :span="12" :xs="24">
            <el-form-item prop="phone" :error="errors.phone" label="Phone">
              <el-input
                v-model="contact.phone"
                type="text"
                :disabled="loading"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </template>
      <!-- CORPORATE END -->

      <el-divider />

      <!-- ADDRESS -->
      <el-row :gutter="20">
        <p class="block-title">Address</p>

        <!-- Postal -->
        <el-col :span="12" :xs="24">
          <el-form-item
            prop="postal_code"
            :error="errors.postal_code"
            label="Postal code"
          >
            <el-input
              v-model="contact.postal_code"
              type="text"
              :disabled="loading"
            />
          </el-form-item>
        </el-col>

        <!-- City -->
        <el-col :span="12" :xs="24">
          <el-form-item prop="city" :error="errors.city" label="City">
            <el-input v-model="contact.city" type="text" :disabled="loading" />
          </el-form-item>
        </el-col>

        <!-- Street -->
        <el-col :span="12" :xs="24">
          <el-form-item
            prop="address"
            :error="errors.address"
            label="Street address"
          >
            <el-input
              v-model="contact.address"
              type="text"
              :disabled="loading"
            />
          </el-form-item>
        </el-col>

        <!-- Apartment -->
        <el-col :span="12" :xs="24">
          <el-form-item
            prop="room"
            :error="errors.room"
            label="Apartment, Floor, Suite, Bldg"
          >
            <el-input v-model="contact.room" type="text" :disabled="loading" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="24">
          <el-form-item class="btn-block">
            <el-button plain @click.prevent="goBack"> Cancel </el-button>
            <el-button
              type="primary"
              plain
              :disabled="loading"
              class="submit-button"
              :icon="loading ? 'LoadingElementIcon' : ''"
              @click.prevent="() => onSubmit(false, false)"
            >
              Save
            </el-button>
            <el-button
              v-if="withProfileVerification && !contact.verification_present"
              type="primary"
              plain
              :disabled="loading"
              class="submit-button"
              :icon="loading ? 'LoadingElementIcon' : ''"
              @click.prevent="() => onSubmit(false, true)"
            >
              Save and verify
            </el-button>
            <el-button
              v-if="
                withProfileVerification &&
                (contact.verification_not_finished ||
                  contact.verification_failed) &&
                contact.verification_url
              "
              type="primary"
              plain
              :disabled="loading"
              class="submit-button"
              :icon="loading ? 'LoadingElementIcon' : ''"
              @click.prevent="verifyProfile(contact.verification_url)"
            >
              Continue verification
            </el-button>

            <el-button
              v-if="
                contact.docs_needed && contact.type === ContactType.Corporate
              "
              type="primary"
              plain
              :disabled="loading"
              class="submit-button"
              :icon="loading ? 'LoadingElementIcon' : ''"
              @click.prevent="() => onSubmit(true, false)"
            >
              Upload verification documents
            </el-button>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <DocumentsPopup v-model="documentsModalIsVisible" />
  </el-card>
</template>
<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue'
import { useContactStore } from '@/store/modules/contact'
import useValidation from '@/hooks/useValidation'
import { ElMessage } from 'element-plus'
import {
  ContactTypeLabels,
  ProfileValidationErrorsKey,
  ContactType
} from '@/api/types'
import { IContactAttributes } from '@/api/contacts'

import { useCountriesStore } from '@/store/modules/countries'
import useRouter from '@/hooks/useRouter'
import { useUserStore } from '@/store/modules/user'
import IconWithHint from '@/components/IconWithHint.vue'
import DocumentsPopup from './DocumentsPopup.vue'
import CountrySelect from './CountrySelect.vue'
import { attachVerifySDK } from '@/utils/third-party'
import { finishItem as finishUserVerification } from '@/api/user_verification'
import { useOnboardingRedirect } from '@/hooks/useOnboardingRedirect'

export default defineComponent({
  name: 'ProfileForm',
  components: {
    IconWithHint,
    DocumentsPopup,
    CountrySelect
  },
  props: {
    goBackOnSave: {
      type: Boolean,
      default: false
    },
    withVerification: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const contactStore = useContactStore()
    const userStore = useUserStore()
    const countriesStore = useCountriesStore()
    const { processOnboarding } = useOnboardingRedirect()
    countriesStore.GetItems()

    attachVerifySDK()

    const countries = computed(() => {
      return countriesStore.forSelector
    })
    const { route, router } = useRouter()
    const loading = ref(false)
    const { provideValidation, setValidationErrors } = useValidation(
      ProfileValidationErrorsKey
    )
    const errors = provideValidation()
    const contact = ref({
      type: ContactType.Personal,
      country_code: '',
      email: userStore.email,
      tin: '',
      vat: '',
      verification_present: false,
      verification_url: '',
      verification_failed: false,
      verification_not_finished: false
    })
    const form = ref(null)
    const haveVat = ref(true)
    const prevVat = ref('')
    const prevTin = ref('')
    const documentsModalIsVisible = ref(false)

    contactStore.FetchContact().then(() => {
      if (contactStore.contact) {
        contact.value = contactStore.contact
        if (!contact.value.email) {
          contact.value.email = userStore.email
        }

        prevVat.value = contact.value.vat
        prevTin.value = contact.value.tin

        if (contact.value.vat === '') haveVat.value = false
      }
    })

    const withProfileVerification = computed(() => {
      return (
        props.withVerification && contact.value.type === ContactType.Personal
      )
    })

    const isEuCountry = computed(() => {
      let country = countriesStore.byIso([contact.value.country_code])[0]
      return country?.attributes?.is_eu || false
    })

    const isZipCountry = computed(() => {
      let country = countriesStore.byIso([contact.value.country_code])[0]
      return country?.attributes?.with_zip ?? true
    })

    const vatIsVisible = computed(() => {
      return isEuCountry.value
    })

    const vatPresent = computed(() => {
      return vatIsVisible.value && haveVat.value
    })

    const tinIsVisible = computed(() => {
      return !vatPresent.value && contact.value.type === ContactType.Corporate
    })

    watch(
      () => vatPresent.value,
      (vatPresent, oldVatPresent) => {
        if (vatPresent) {
          contact.value.vat = prevVat.value
        } else {
          if (contact.value.vat !== '') {
            prevVat.value = contact.value.vat
          }
          contact.value.vat = ''
        }
      }
    )

    watch(
      () => tinIsVisible.value,
      (tinIsVisible, oldTinIsVisible) => {
        if (tinIsVisible) {
          contact.value.tin = prevTin.value
        } else {
          if (contact.value.tin !== '') {
            prevTin.value = contact.value.tin
          }
          contact.value.tin = ''
        }
      }
    )

    watch(
      () => [contact.value.type, isEuCountry.value],
      ([contactType, isEuCountry], [_1, _2]) => {
        if (
          contactType === ContactType.Corporate &&
          isEuCountry &&
          contact.value.vat === '' &&
          contact.value.tin === ''
        ) {
          haveVat.value = true
        }
      }
    )

    const tinValidationRule = computed(() => {
      let msg =
        contact.value.tin.length > 0
          ? 'TAX ID must be at least 5 characters'
          : "If you don't have VAT, TAX ID is required"

      return {
        required: tinIsVisible.value && vatIsVisible.value && !haveVat.value,
        min: 5,
        message: msg,
        trigger: 'change'
      }
    })

    const vatValidationRule = computed(() => {
      let msgVat =
        contact.value.vat.length > 0
          ? 'VAT Number must be at least 5 characters'
          : 'VAT field is required'

      return {
        required: vatPresent.value,
        min: 5,
        message: msgVat,
        trigger: 'change'
      }
    })

    const handleModalEvent = (message: string) => {
      switch (message) {
        case window.veriffSDK.MESSAGES.CANCELED:
          window.location.reload()
          break
        case window.veriffSDK.MESSAGES.FINISHED:
          finishUserVerification().then(() => {
            window.location.reload()
          })
      }
    }

    const verifyProfile = (verificationUrl: string) => {
      window.veriffSDK.createVeriffFrame({
        url: verificationUrl || contact.value.verification_url,
        onEvent: handleModalEvent
      })
    }

    const onSubmit = (
      withVerificationDocuments: boolean,
      withProfileVerification: boolean
    ) => {
      // @ts-ignore: Object is possibly 'null'.
      form.value.validate(async (valid: boolean) => {
        if (valid) {
          errors.value = {}
          loading.value = true
          try {
            const payload = contact.value as IContactAttributes
            payload.with_profile_verification = withProfileVerification
            const response = await contactStore.SaveContact(payload)
            ElMessage.success({
              message: 'Contact has been changed',
              type: 'success'
            })
            await userStore.UpdateContactState()
            await userStore.GetUserInfo(false)
            if (withVerificationDocuments) {
              documentsModalIsVisible.value = true
            }

            if (
              await processOnboarding(
                'Top up your balance and finish onboarding'
              )
            ) {
              return
            }

            if (route.value.query.redirect) {
              router.push(String(route.value.query.redirect))
            }
            if (
              withProfileVerification &&
              response.data.attributes.verification_url
            ) {
              verifyProfile(response.data.attributes.verification_url)
            } else if (props.goBackOnSave) {
              context.emit('go-back')
            }
            errors.value = {}
          } catch (err: any) {
            setValidationErrors(err, errors)
          } finally {
            loading.value = false
          }
        }
      })
    }

    const rule = {
      required: true,
      message: 'This field is required',
      trigger: 'change'
    }

    const defaultRules = {
      company_name: rule,
      country_code: rule,
      email: rule,
      first_name: rule,
      last_name: rule,
      registration_number: rule,
      type: rule,
      city: rule,
      address: rule,
      representative_last_name: rule,
      representative_first_name: rule,
      position: rule
    }

    const rules = computed(() => {
      const zipRules = isZipCountry.value ? { postal_code: rule } : {}

      if (contact.value.type === ContactType.Personal) {
        return Object.assign({}, defaultRules, zipRules, {
          room: rule,
          birthday: rule,
          vat: vatValidationRule.value
        })
      } else {
        return Object.assign({}, defaultRules, zipRules, {
          vat: vatValidationRule.value,
          tin: tinValidationRule.value
        })
      }
    })

    const goBack = () => {
      context.emit('go-back')
    }

    return {
      loading,
      contact,
      errors,
      onSubmit,
      ContactTypeLabels,
      countries,
      ContactType,
      form,
      rules,
      goBack,
      vatIsVisible,
      tinIsVisible,
      haveVat,
      isEuCountry,
      documentsModalIsVisible,
      verifyProfile,
      withProfileVerification
    }
  }
})
</script>

<style lang="scss" scoped>
.btn-block {
  display: flex;
  margin-top: 10px;
  justify-content: space-between;
}
.block-title {
  width: 100%;
}
.el-divider {
  margin: 12px 0 0 0;
}
:deep(.el-form-item) {
  margin-bottom: 10px;
  :deep(.el-form-item__label) {
    padding: 0;
  }
}
</style>
